import { Component } from '@theme/component';

/**
 * Alphabet filter bar for the Brands directory. Finds its target grid
 * positionally -- the collection-list section rendered immediately after
 * this one's own `.shopify-section` wrapper -- rather than by id, so the
 * shared collection-list section needs no changes of its own.
 *
 * @typedef {{ letterButtons: HTMLButtonElement[] }} BrandIndexRefs
 * @extends {Component<BrandIndexRefs>}
 */
export class BrandIndexComponent extends Component {
  requiredRefs = ['letterButtons'];

  /** @type {HTMLElement[]} */
  #items = [];

  connectedCallback() {
    super.connectedCallback();

    const grid = this.#findTargetGrid();
    this.#items = grid ? Array.from(grid.querySelectorAll('.resource-list__item')) : [];

    this.#enableAvailableLetters();
  }

  #findTargetGrid() {
    const ownSection = this.closest('.shopify-section');
    const nextSection = ownSection?.nextElementSibling;
    return nextSection instanceof HTMLElement ? nextSection : null;
  }

  #enableAvailableLetters() {
    const available = new Set(this.#items.map((item) => this.#firstLetter(item)));

    for (const button of this.refs.letterButtons) {
      const letter = button.dataset.letter;
      if (letter === 'all') continue;
      if (available.has(letter)) button.disabled = false;
    }
  }

  /** @param {HTMLElement} item */
  #firstLetter(item) {
    return (item.textContent || '').trim().charAt(0).toUpperCase();
  }

  /** @param {Event} event */
  selectLetter(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const letter = button.dataset.letter || 'all';

    for (const candidate of this.refs.letterButtons) {
      const isActive = candidate === button;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-pressed', String(isActive));
    }

    for (const item of this.#items) {
      item.hidden = letter !== 'all' && this.#firstLetter(item) !== letter;
    }
  }
}

if (!customElements.get('brand-index-component')) {
  customElements.define('brand-index-component', BrandIndexComponent);
}
